import type { EffectiveEntitlements } from '@kora/types';
import { apiRequest } from './client.js';

/**
 * Tenant-side subscription self-service (Phase C-3). Consumes the C-2
 * GET /v1/subscriptions/me endpoint. The shape mirrors the API's
 * formatTenantSubscription serializer — keep in sync with
 * apps/api/src/services/subscriptionStatus.ts.
 *
 * These enums are the canonical web copies of the Prisma/Zod enums; the
 * operator module (api/platform.ts) re-exports them so the shared
 * StatusBadge and the platform mutation forms reference one source.
 */

export type SubscriptionTier = 'starter' | 'growth' | 'enterprise';

export type SubscriptionStatus =
  | 'trial'
  | 'active'
  | 'past_due'
  | 'read_only'
  | 'suspended'
  | 'cancelled';

export type PaymentProvider = 'manual' | 'paddle' | 'stripe';

/**
 * The server's answer to "what IS this billing period" — rendered, never
 * re-derived. The client must NOT reinterpret currentPeriodEnd /
 * cancelScheduledAt to decide a label: doing so is what shipped "Renews <a past
 * date>" and a resume button the server refuses (#371/#372).
 */
export type PeriodState =
  | 'trial'
  | 'cancel_scheduled'
  | 'cancel_finalising'
  | 'renews'
  | 'period_ended'
  | 'none';

export interface SubscriptionMe {
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  effectiveStatus: SubscriptionStatus;
  periodState: PeriodState;
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  daysRemainingInTrial: number | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  isFoundingCustomer: boolean;
  paymentProvider: PaymentProvider;
  billingCurrency: string;
  cancelledAt: string | null;
  // Self-serve cancel (ADR-0026 follow-up): when set, the plan is scheduled to
  // cancel on this date but access continues until then (paid-through, NOT a
  // refund). Drives the /billing "cancels on <date>" notice.
  cancelScheduledAt: string | null;
  // Phase D-5 PR-2b: dunning fields for the past_due/read_only banner.
  pastDueAt: string | null;
  readOnlyAt: string | null;
  graceDaysRemaining: number | null; // only when status='past_due'
  // Phase D-3: resolved entitlements (tier defaults + per-tenant overrides).
  // Added to the API response in D-3; the web type was not updated until D-4.
  effectiveEntitlements: EffectiveEntitlements;
  // Phase D-4: cap-counted seat usage (active + on_leave) and the resolved
  // max_employees cap (null = unlimited). Drive the cap-warning banner.
  employeeCount: number;
  employeeCap: number | null;
  // ADR-0032 D7: how this tenant pays — keyed on the contract, not the tier.
  // Render it; never re-derive it from tier/paymentProvider (a tier-keyed branch
  // sent six invoiced non-Enterprise tenants to a Paddle checkout).
  billingChannel: BillingChannel;
  // ADR-0032 D8: the oldest unpaid (issued/overdue) invoice, or null. No amounts
  // (this payload also reaches the employee layout) — /billing reads those from
  // GET /v1/invoices/me.
  openInvoice: OpenInvoiceRef | null;
}

/** ADR-0032 D7 — server-derived. `invoiced` = sales-led, paid by invoice + bank transfer. */
export type BillingChannel = 'self_serve' | 'invoiced';

export interface OpenInvoiceRef {
  id: string;
  number: string;
  status: 'issued' | 'overdue';
  dueDate: string;
}

/** GET /v1/subscriptions/me — the authenticated tenant's own subscription. */
export function getSubscriptionMe(): Promise<SubscriptionMe> {
  return apiRequest<SubscriptionMe>('/v1/subscriptions/me');
}

/** Billing interval for a checkout (ADR-0026 PR-3b-3). */
export type BillingInterval = 'month' | 'year';

/**
 * The server-authoritative context the Paddle overlay needs (ADR-0026 PR-3b-2).
 * `quantity` = getActiveSeatCount (the tenant's current headcount) — also the
 * floor the under-buy snap-back enforces. `subscriptionId` rides into the
 * checkout's customData so the webhook can link the paid subscription.
 */
export interface CheckoutContext {
  priceId: string;
  quantity: number;
  subscriptionId: string;
}

/** GET /v1/subscriptions/checkout-context — resolve tier+interval → { priceId, quantity, subscriptionId }. */
export function getCheckoutContext(tier: SubscriptionTier, interval: BillingInterval): Promise<CheckoutContext> {
  return apiRequest<CheckoutContext>(
    `/v1/subscriptions/checkout-context?tier=${encodeURIComponent(tier)}&interval=${encodeURIComponent(interval)}`,
  );
}

/** The scheduled-cancellation result (ADR-0026 follow-up). `accessUntil` = the
 * period-end date access continues to (paid-through); NOT a refund. */
export interface CancelResult {
  effectiveAt: string;
  accessUntil: string;
  message: string;
  /**
   * The server could not name a FUTURE effective date (the scheduled moment has
   * already passed, or Paddle returned no scheduledChange and the fallback chain
   * bottomed out). When true, `effectiveAt`/`accessUntil` are still the recorded
   * moment but must NOT be rendered as "access until <date>" — `message` is
   * already worded correctly for this case. Optional so a response from an older
   * deploy still types.
   */
  finalising?: boolean;
}

/** POST /v1/subscriptions/cancel — schedule cancellation at period end (owner/admin). */
export function cancelSubscription(): Promise<CancelResult> {
  return apiRequest<CancelResult>('/v1/subscriptions/cancel', { method: 'POST', body: {} });
}

/** POST /v1/subscriptions/resume — reverse a pending cancellation (owner/admin). */
export function resumeSubscription(): Promise<{ message: string }> {
  return apiRequest<{ message: string }>('/v1/subscriptions/resume', { method: 'POST', body: {} });
}
